const MISSING_CHUNK_RELOAD_STORAGE_KEY = "fawkes.missingChunkReloadUrl";

const ASSET_URL_PATTERN = /(?:https?:\/\/[^/\s]+)?(\/assets\/[^\s"'?]+)/i;

let reloadInFlight = false;
let listenerInstalled = false;

function errorText(error: unknown): string {
	if (error instanceof Error) {
		return error.message;
	}
	if (
		typeof error === "object" &&
		error !== null &&
		"payload" in error &&
		(error as { payload?: unknown }).payload !== undefined
	) {
		return errorText((error as { payload: unknown }).payload);
	}
	if (typeof error === "string") {
		return error;
	}
	return error == null ? "" : String(error);
}

export function extractMissingChunkUrl(error: unknown): string | null {
	const text = errorText(error);
	const match = text.match(ASSET_URL_PATTERN);
	return match?.[1] ?? match?.[0] ?? null;
}

export function isMissingChunkError(error: unknown): boolean {
	const text = errorText(error);
	if (!text) {
		return false;
	}

	const isDynamicImportFailure =
		text.includes("dynamically imported module") ||
		text.includes("Failed to fetch dynamically imported module") ||
		text.includes("error loading dynamically imported module");

	if (isDynamicImportFailure) {
		return true;
	}

	const assetUrl = extractMissingChunkUrl(error);
	return Boolean(assetUrl?.endsWith(".js"));
}

export function handleMissingChunkError(error: unknown): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	if (!isMissingChunkError(error)) {
		return false;
	}
	if (reloadInFlight) {
		return true;
	}

	const url = extractMissingChunkUrl(error) ?? "unknown";
	const previous = window.sessionStorage.getItem(
		MISSING_CHUNK_RELOAD_STORAGE_KEY,
	);
	if (previous === url) {
		return false;
	}

	reloadInFlight = true;
	window.sessionStorage.setItem(MISSING_CHUNK_RELOAD_STORAGE_KEY, url);
	window.location.reload();
	return true;
}

export function installMissingChunkReloadListener(): void {
	if (typeof window === "undefined" || listenerInstalled) {
		return;
	}
	listenerInstalled = true;

	window.addEventListener("vite:preloadError", (event) => {
		event.preventDefault();
		handleMissingChunkError(
			"payload" in event
				? ((event as Event & { payload?: unknown }).payload ?? event)
				: event,
		);
	});

	window.addEventListener("unhandledrejection", (event) => {
		if (!isMissingChunkError(event.reason)) {
			return;
		}
		event.preventDefault();
		handleMissingChunkError(event.reason);
	});
}
